import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, PlusCircle, Clock, BookOpen, Boxes, Truck, Trash2, Receipt,
  Building2, BarChart3, ShieldCheck, Menu as MenuIcon, LogOut, Store, Package,
  Sparkles, ClipboardCheck, Inbox, UploadCloud,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import NotificationsBell from "@/components/NotificationsBell";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "kitchen", "finance", "client"] },
  { to: "/my-requests", label: "Menu Requests", icon: Inbox, roles: ["client"] },
  { to: "/quick-order", label: "Quick Order", icon: PlusCircle, roles: ["admin", "kitchen"] },
  { to: "/orders", label: "Orders & KDS", icon: Clock, roles: ["admin", "kitchen", "client"] },
  { to: "/menu", label: "Menu & Recipes", icon: BookOpen, roles: ["admin", "kitchen", "finance", "client"] },
  { to: "/ai-import", label: "AI Import", icon: Sparkles, roles: ["admin", "kitchen"] },
  { to: "/upload-centre", label: "AI Upload Centre", icon: UploadCloud, roles: ["admin", "kitchen", "finance"] },
  { to: "/approvals", label: "Approvals", icon: ClipboardCheck, roles: ["admin", "kitchen"] },
  { to: "/ingredients", label: "Ingredients", icon: Package, roles: ["admin", "kitchen", "finance"] },
  { to: "/inventory", label: "Inventory", icon: Boxes, roles: ["admin", "kitchen", "finance", "client"] },
  { to: "/purchases", label: "Purchases", icon: Truck, roles: ["admin", "finance"] },
  { to: "/wastage", label: "Wastage", icon: Trash2, roles: ["admin", "kitchen", "finance"] },
  { to: "/billing", label: "Client Billing", icon: Receipt, roles: ["admin", "finance", "client"] },
  { to: "/clients", label: "Clients", icon: Building2, roles: ["admin", "finance"] },
  { to: "/brands", label: "Brands", icon: Store, roles: ["admin", "finance"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin", "finance"] },
  { to: "/settings", label: "Settings & Audit", icon: ShieldCheck, roles: ["admin"] },
];

const MODULE_OF = { "/orders": "orders", "/menu": "menu", "/inventory": "inventory", "/billing": "billing", "/wastage": "wastage", "/reports": "reports" };

export default function Layout({ children }) {
  const { user, logout, roleLabel, modules } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const allowed = modules;
  const items = NAV.filter((n) => n.roles.includes(user.role)).filter((n) => {
    if (user.role !== "client" || allowed === null) return true;
    const m = MODULE_OF[n.to];
    return !m || allowed.includes(m);
  });

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center gap-2.5 px-5 border-b border-border shrink-0">
        <img src="/df-logo-header.png" alt="DishFactory" className="h-7 w-auto" />
        <span className="text-[10px] font-extrabold uppercase tracking-widest text-primary-foreground bg-primary rounded-md px-1.5 py-0.5 font-display">OS</span>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary/15 text-amber-300 border border-primary/30"
                  : "text-slate-400 hover:text-white hover:bg-secondary/60 border border-transparent"
              }`
            }
          >
            <n.icon className="h-[18px] w-[18px] shrink-0" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border shrink-0">
        <div className="px-2 py-2 mb-2">
          <p className="text-sm font-medium text-white truncate">{user.name || user.email}</p>
          <p className="text-xs text-amber-400/90 font-mono">{roleLabel}</p>
        </div>
        <Button variant="outline" size="sm" className="w-full justify-start gap-2" onClick={doLogout} data-testid="logout-btn">
          <LogOut className="h-4 w-4" /> Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background factory-grid">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-border bg-card/60 backdrop-blur-sm flex-col fixed inset-y-0">
        {SidebarInner}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-card border-r border-border">{SidebarInner}</aside>
        </div>
      )}

      <div className="flex-1 lg:pl-64 min-w-0">
        <header className="h-16 sticky top-0 z-40 bg-card/90 backdrop-blur-md border-b border-border px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden text-slate-300" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
              <MenuIcon className="h-6 w-6" />
            </button>
            <div className="hidden sm:flex items-center gap-2 text-xs font-mono">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-emerald-400 uppercase tracking-wider">Operational</span>
              <span className="text-muted-foreground">· Al Quoz Central</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationsBell />
            {["admin", "kitchen"].includes(user.role) && (
              <Button
                size="sm"
                onClick={() => navigate("/quick-order")}
                data-testid="header-quick-order-btn"
                className="gap-1.5 bg-primary text-primary-foreground hover:bg-amber-400 font-semibold"
              >
                <PlusCircle className="h-4 w-4" /> <span className="hidden sm:inline">Quick Order</span>
              </Button>
            )}
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1500px] mx-auto animate-fade-up" key={location.pathname}>
          {children}
        </main>
      </div>
    </div>
  );
}
